/** @jsx jsx */
import React, { type Dispatch, type SetStateAction, type ReactNode } from 'react';
import { styled, css, jsx } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import type { LegacyData } from '@atlassian/jira-development-details-loader/src/common/types/index.tsx';
import { FEATURE_FLAGS_PANEL_DATA_TYPE } from '@atlassian/jira-development-details/src/common/model/constants.tsx';
import type { PanelDataTypes } from '@atlassian/jira-development-details/src/main/model/types.tsx';
import { fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import type { featureFlags_softwareReleasesVersionDetailIssueList_devOpsSummarisedEntities$key } from '@atlassian/jira-relay/src/__generated__/featureFlags_softwareReleasesVersionDetailIssueList_devOpsSummarisedEntities.graphql';
import type { featureFlags_softwareReleasesVersionDetailIssueList_jiraIssue$key } from '@atlassian/jira-relay/src/__generated__/featureFlags_softwareReleasesVersionDetailIssueList_jiraIssue.graphql';
import {
	VIEWPORT_WIDTH_MEDIUM,
	ISSUE_CARD_DATA_COMPONENT_SELECTOR,
} from '../../../../common/constants.tsx';
import { AddFeatureFlagButton, AddFeatureFlagDropdown } from './add-feature-flag/index.tsx';
import { MultipleFlags } from './multiple-flags/index.tsx';
import { SingleFlag } from './single-flag/index.tsx';

type Props = {
	devOpsSummarisedEntities: featureFlags_softwareReleasesVersionDetailIssueList_devOpsSummarisedEntities$key;
	jiraIssue: featureFlags_softwareReleasesVersionDetailIssueList_jiraIssue$key;
	onClick: (dataType: PanelDataTypes, reviewData?: LegacyData) => void;
	setFeatureFlagEmptyStateModalOpen: Dispatch<SetStateAction<boolean>>;
	rowNum: number;
};

export const FeatureFlags = ({
	devOpsSummarisedEntities,
	jiraIssue,
	onClick,
	setFeatureFlagEmptyStateModalOpen,
	rowNum,
}: Props) => {
	const issue = useFragment(
		graphql`
			fragment featureFlags_softwareReleasesVersionDetailIssueList_jiraIssue on JiraIssue {
				summarisedFeatureFlags {
					...multipleFlags_softwareReleasesVersionDetailIssueList_summarisedFlags
					...singleFlag_softwareReleasesVersionDetailIssueList
					totalCount
				}
				fieldsById(ids: ["priority", "summary", "assignee", "status", "issuetype"])
					@required(action: THROW) {
					edges @required(action: THROW) {
						node {
							fieldId @required(action: THROW)
							... on JiraSingleLineTextField {
								...addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssueSummary
							}
						}
					}
				}
				...addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_jiraIssue
				...multipleFlags_softwareReleasesVersionDetailIssueList_jiraIssue
			}
		`,
		jiraIssue,
	);

	const { providers } = useFragment(
		graphql`
			fragment featureFlags_softwareReleasesVersionDetailIssueList_devOpsSummarisedEntities on DevOpsSummarisedEntities {
				providers @required(action: THROW) {
					featureFlagProviders @required(action: THROW) {
						__typename
						...addFeatureFlag_softwareReleasesVersionDetailIssueList_AddFeatureFlagDropdown_devOpsFeatureFlagProviders
					}
				}
			}
		`,
		devOpsSummarisedEntities,
	);

	const summarisedFlags = issue.summarisedFeatureFlags;
	const featureFlagCount = summarisedFlags?.totalCount ?? 0;
	const hasFeatureFlags =
		summarisedFlags != null && featureFlagCount != null && featureFlagCount > 0;
	const featureFlagProviders = (providers.featureFlagProviders ?? []).filter(Boolean);
	const hasFeatureFlagProviders = featureFlagProviders.length > 0;

	const renderContent = () => {
		const fields = issue.fieldsById.edges.map((edge) => edge?.node).filter(Boolean);
		const jiraIssueSummary = fields.find((fieldNode) => fieldNode?.fieldId === 'summary');

		// Render the single or multiple flags component if the issue has at least one linked
		// feature flag
		if (hasFeatureFlags) {
			return (
				<Button
					appearance="subtle-link"
					spacing="none"
					onClick={(_, analyticsEvent: UIAnalyticsEvent) => {
						onClick(FEATURE_FLAGS_PANEL_DATA_TYPE);
						fireUIAnalytics(analyticsEvent, 'featureFlagSummaryCell');
					}}
				>
					{featureFlagCount === 1 ? (
						<SingleFlag summarisedFlags={summarisedFlags} />
					) : (
						<MultipleFlags jiraIssue={issue} summarisedFlags={summarisedFlags} />
					)}
				</Button>
			);
		}

		// Render the "add flag" dropdown if there's at least one feature flag provider and the
		// issue has no linked feature flags
		if (hasFeatureFlagProviders) {
			return jiraIssueSummary != null ? (
				<OnHoverVisibility>
					<AddFeatureFlagDropdown
						devOpsFeatureFlagProviders={featureFlagProviders}
						jiraIssue={issue}
						jiraIssueSummary={jiraIssueSummary}
						onOpenFeatureFlagEmptyStateModal={() => setFeatureFlagEmptyStateModalOpen(true)}
					/>
				</OnHoverVisibility>
			) : null;
		}

		// If the issue has no linked feature flags and there are no linked providers, return the
		// "add feature flag provider" button
		return (
			<OnHoverVisibility>
				<AddFeatureFlagButton
					onOpenFeatureFlagEmptyStateModal={() => setFeatureFlagEmptyStateModalOpen(true)}
					rowNum={rowNum}
				/>
			</OnHoverVisibility>
		);
	};

	return <FeatureFlagsContainer>{renderContent()} </FeatureFlagsContainer>;
};

export const OnHoverVisibility = ({ children }: { children: ReactNode }) => (
	<div css={styles}>{children}</div>
);

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled, @atlaskit/ui-styling-standard/no-exported-styles -- Ignored via go/DSP-18766
export const FeatureFlagsContainer = styled.div({
	display: 'flex',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (min-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 13}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		minWidth: `${gridSize * 13}px`,
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`@media screen and (max-width: ${VIEWPORT_WIDTH_MEDIUM}px)`]: {
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		width: `${gridSize * 3}px`,
		// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
		minWidth: `${gridSize * 3}px`,
	},
});

const styles = css({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- Ignored via go/DSP-18766
	'& > *': {
		visibility: 'hidden',
	},
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-values, @atlaskit/ui-styling-standard/no-nested-selectors, @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	[`[data-component-selector="${ISSUE_CARD_DATA_COMPONENT_SELECTOR}"]:hover & > *`]: {
		visibility: 'visible',
	},
});
